import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Fade,
  Zoom,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Sick,
  Home,
  AccessTime,
  BeachAccess,
  Gavel,
  PictureAsPdf,
} from '@mui/icons-material';
import Button from '@mui/material/Button';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import { getEmployeeAbsences, getMyAbsences } from '../services/absenceAPIs';
import { getEmployeeLeaves, getMyLeaves } from '../services/leavesAPIs';
import DateFormatter from './DateFormatter';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExportButtons from './btns/ExportButtons';
import marabesLogo from '../assets/images/logos/Marabes.png';
import '../assets/styles/unified-controls.css';

// Helper: Generate month options for specific year
const generateMonthOptionsForYear = (year) => {
  if (typeof year !== 'number') return [];
  const months = [];
  for (let i = 1; i <= 12; i++) {
    months.push({
      value: `${year}-${String(i).padStart(2, '0')}`,
      label: new Date(year, i - 1, 1).toLocaleString('default', {
        month: 'long',
      }),
    });
  }
  return months;
};

const getDynamicTheme = (mode) => createTheme({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
        primary: { main: '#1976d2', light: '#42a5f5', dark: '#1565c0' },
        secondary: { main: '#f50057' },
        success: { main: '#2e7d32' },
        warning: { main: '#ed6c02' },
        error: { main: '#d32f2f' },
        info: { main: '#0288d1' },
        background: { default: '#f5f7fa', paper: '#ffffff' },
      }
      : {
        primary: { main: '#90caf9', light: '#e3f2fd', dark: '#42a5f5' },
        secondary: { main: '#f48fb1' },
        success: { main: '#81c784' },
        warning: { main: '#ffb74d' },
        error: { main: '#e57373' },
        info: { main: '#4fc3f7' },
        background: { default: '#121212', paper: '#1e1e1e' },
        text: { primary: '#ffffff', secondary: '#b0bec5' },
      }),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: { fontWeight: 600, fontSize: '1.25rem' },
    caption: { fontSize: '0.875rem' },
    body2: { fontSize: '0.9rem' },
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          overflow: 'hidden',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          fontWeight: 600,
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f1f5f9',
          padding: '12px 16px',
        }),
        body: ({ theme }) => ({ padding: '12px 16px', color: theme.palette.text.primary }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.85rem',
          padding: '4px 8px',
        },
      },
    },
  },
});

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`
    : `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
  color: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#ffffff',
  padding: '16px 24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: theme.palette.background.default },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transition: 'background-color 0.2s ease',
  },
}));

const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const getStatusConfig = status => {
  const upper = (status || '').toUpperCase();
  switch (upper) {
    case 'APPROVED':
      return { label: 'Recorded', color: 'default' };
    case 'PENDING':
      return { label: 'Recorded', color: 'default' };
    case 'REJECTED':
      return { label: 'Recorded', color: 'default' };
    case 'CANCELLED':
      return { label: 'Recorded', color: 'default' };
    default:
      return { label: status || 'Recorded', color: 'default' };
  }
};

const getMonthYearKey = dateStr => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const AbsenceRecordsModal = ({
  open,
  onClose,
  employeeId,
  absenceType,
  employeeName,
  userRole,
  minMinutesLate = 0,
  selectedYear = new Date().getFullYear(), // Accept year from parent
}) => {
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');

  // Get external theme mode
  const externalTheme = useTheme();
  const modalTheme = React.useMemo(() => getDynamicTheme(externalTheme.palette.mode), [externalTheme.palette.mode]);

  // Compute UI state based on selectedYear
  const isAllTime = selectedYear === 'All Time';
  const currentYear = new Date().getFullYear();
  const displayYear = isAllTime ? 'All Time' : selectedYear;

  // ✅ PASS NULL TO BACKEND WHEN "ALL TIME" IS SELECTED
  const yearParam = isAllTime ? null : selectedYear;

  const monthOptions = !isAllTime && typeof selectedYear === 'number'
    ? generateMonthOptionsForYear(selectedYear)
    : [];

  // Reset month filter when year changes
  useEffect(() => {
    setSelectedMonth('');
  }, [selectedYear]);

  useEffect(() => {
    const fetchData = async () => {
      if (!open) return;
      setLoading(true);
      setError(null);
      try {
        let data = [];

        // ✅ PASS yearParam (null for All Time) TO API CALLS
        if (absenceType === 'ANNUAL_LEAVE' || absenceType === 'UNPAID_LEAVE') {
          const leaveType = absenceType;
          if (userRole === 'EMPLOYEE') {
            const myLeaves = await getMyLeaves();
            data = myLeaves
              .filter(leave => {
                // Filter by year if not All Time
                if (!isAllTime && typeof selectedYear === 'number') {
                  const leaveYear = new Date(leave.startDate).getFullYear();
                  if (leaveYear !== selectedYear) return false;
                }
                return (
                  leave.type === leaveType &&
                  leave.status?.toUpperCase() === 'APPROVED'
                );
              })
              .map(leave => ({
                id: leave.requestId,
                ty_pe: leaveType,
                startDate: leave.startDate,
                endDate: leave.endDate,
                days: leave.days || 0,
                hours: 0,
                status: leave.status?.toUpperCase() || 'APPROVED',
                reason: leave.justification || leave.reason || 'N/A',
                createdAt: leave.createdAt,
              }))
              .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
          } else {
            if (!employeeId) throw new Error('Employee ID is required');
            const allLeaves = await getEmployeeLeaves(employeeId);
            data = allLeaves
              .filter(leave => {
                // Filter by year if not All Time
                if (!isAllTime && typeof selectedYear === 'number') {
                  const leaveYear = new Date(leave.startDate).getFullYear();
                  if (leaveYear !== selectedYear) return false;
                }
                return (
                  leave.type === leaveType &&
                  leave.status?.toUpperCase() === 'APPROVED'
                );
              })
              .map(leave => ({
                id: leave.requestId,
                ty_pe: leaveType,
                startDate: leave.startDate,
                endDate: leave.endDate,
                days: leave.days || 0,
                hours: 0,
                status: leave.status?.toUpperCase() || 'APPROVED',
                reason: leave.justification || leave.reason || 'N/A',
                createdAt: leave.createdAt,
              }))
              .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
          }
        } else {
          let absences = [];
          if (userRole === 'EMPLOYEE') {
            absences = await getMyAbsences(yearParam);
          } else {
            if (!employeeId) throw new Error('Employee ID is required');
            absences = await getEmployeeAbsences(employeeId, yearParam);
          }

          let filteredAbsences = absences;
          if (absenceType) {
            const upperType = absenceType.toUpperCase();
            if (upperType === 'SICKNESS') {
              filteredAbsences = absences.filter(
                a => a.ty_pe?.toUpperCase() === 'SICKNESS'
              );
            } else if (upperType === 'PERSONAL') {
              filteredAbsences = absences.filter(a =>
                ['PERSONAL', 'PERSONAL_COMMITMENT'].includes(
                  a.ty_pe?.toUpperCase()
                )
              );
            } else if (upperType === 'LATENESS') {
              filteredAbsences = absences.filter(
                a => a.ty_pe?.toUpperCase() === 'LATENESS'
              );
            } else if (upperType === 'SUSPENSION') {
              filteredAbsences = absences.filter(
                a => a.ty_pe?.toUpperCase() === 'SUSPENSION'
              );
            }
          }

          data = filteredAbsences
            .map(absence => ({
              id: absence.id || absence.requestId,
              ty_pe: absence.ty_pe?.toUpperCase(),
              startDate: absence.startDate || absence.date,
              endDate:
                absence.endDate || absence.startDate || absence.date,
              hours: absence.hours || 0,
              days:
                absence.days ||
                (absence.hours ? absence.hours / 8 : 1),
              status: absence.status?.toUpperCase() || 'PENDING',
              reason:
                absence.justification || absence.reason || 'N/A',
              createdAt: absence.createdAt,
            }))
            .sort((a, b) => {
              const dateA = new Date(a.startDate || a.date || a.createdAt);
              const dateB = new Date(b.startDate || b.date || b.createdAt);
              return dateB - dateA;
            });

          // if this is a lateness modal opened from the 10+ table, keep only records >= minMinutesLate
          if (absenceType === 'LATENESS' && minMinutesLate > 0) {
            data = data.filter(r => {
              const minutes = (r.hours || 0) * 60;
              return Math.round(minutes) >= minMinutesLate;
            });
          }
        }

        setRecords(data);
      } catch (err) {
        console.error('Failed to fetch records:', err);
        setError('Failed to load records. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    // Critical: Include selectedYear in dependencies
    fetchData();
  }, [open, employeeId, absenceType, userRole, minMinutesLate, selectedYear]);

  useEffect(() => {
    if (selectedMonth) {
      const filtered = records.filter(record => {
        const key = getMonthYearKey(record.startDate || record.date);
        return key === selectedMonth;
      });
      setFilteredRecords(filtered);
    } else {
      setFilteredRecords(records);
    }
  }, [records, selectedMonth]);

  const getTypeLabel = ty_pe => {
    if (ty_pe === 'ANNUAL_LEAVE') return 'Annual Leave';
    if (ty_pe === 'UNPAID_LEAVE') return 'Unpaid Leave';
    switch (ty_pe) {
      case 'SICKNESS':
        return 'Sick Leave';
      case 'PERSONAL':
      case 'PERSONAL_COMMITMENT':
        return 'Personal Leave';
      case 'LATENESS':
        return 'Lateness';
      case 'SUSPENSION':
        return 'Suspension';
      default:
        return ty_pe || 'Unknown';
    }
  };

  const getTypeColor = ty_pe => {
    if (ty_pe === 'ANNUAL_LEAVE') return 'success';
    if (ty_pe === 'UNPAID_LEAVE') return 'default';
    switch (ty_pe) {
      case 'SICKNESS':
        return 'error';
      case 'PERSONAL':
      case 'PERSONAL_COMMITMENT':
        return 'warning';
      case 'LATENESS':
        return 'info';
      case 'SUSPENSION':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getTypeIcon = ty_pe => {
    if (ty_pe === 'ANNUAL_LEAVE')
      return <BeachAccess fontSize="small" sx={{ mr: 0.5 }} />;
    if (ty_pe === 'UNPAID_LEAVE')
      return <Home fontSize="small" sx={{ mr: 0.5, color: '#6B7280' }} />;
    switch (ty_pe) {
      case 'SICKNESS':
        return <Sick fontSize="small" sx={{ mr: 0.5 }} />;
      case 'PERSONAL':
      case 'PERSONAL_COMMITMENT':
        return <Home fontSize="small" sx={{ mr: 0.5 }} />;
      case 'LATENESS':
        return <AccessTime fontSize="small" sx={{ mr: 0.5 }} />;
      case 'SUSPENSION':
        return <Gavel fontSize="small" sx={{ mr: 0.5 }} />;
      default:
        return null;
    }
  };

  // Enhanced modal title with year context
  const getModalTitle = () => {
    let baseTitle = 'Absence Records';
    if (absenceType === 'ANNUAL_LEAVE') baseTitle = 'Annual Leave Records';
    else if (absenceType === 'UNPAID_LEAVE') baseTitle = 'Unpaid Leave Records';
    else if (absenceType) {
      switch (absenceType.toUpperCase()) {
        case 'SICKNESS': baseTitle = 'Sick Leave Records'; break;
        case 'PERSONAL': baseTitle = 'Personal Leave Records'; break;
        case 'LATENESS': baseTitle = 'Lateness Records'; break;
        case 'SUSPENSION': baseTitle = 'Suspension Records'; break;
      }
    }
    return isAllTime ? `${baseTitle} (All Time)` : `${baseTitle} (${selectedYear})`;
  };

  const getTotalDuration = () => {
    const data = selectedMonth ? filteredRecords : records;
    if (absenceType === 'LATENESS') {
      const totalMinutes = data.reduce(
        (sum, r) => sum + (r.hours ? Math.round(r.hours * 60) : 0),
        0
      );
      return `${totalMinutes} minutes`;
    } else {
      const totalDays = data.reduce((sum, r) => sum + (r.days || 0), 0);
      if (Number.isInteger(totalDays)) {
        return `${totalDays} days`;
      } else {
        const totalHours = Math.round(totalDays * 8);
        return `${totalHours} hours`;
      }
    }
  };

  const getDurationLabel = record => {
    if (record.hours > 0) {
      return `${record.hours} hour${record.hours === 1 ? '' : 's'}`;
    } else {
      const d = record.days || 0;
      return `${d} day${d === 1 ? '' : 's'}`;
    }
  };

  const getBase64FromUrl = async (url) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn('Could not load image for PDF:', url);
      return null;
    }
  };

  const exportToPDF = async () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;
    const today = new Date().toLocaleDateString('en-GB');
    const axoBase64 = await getBase64FromUrl('/cLogo.png');

    // --- Header logos ---
    try {
      doc.addImage(marabesLogo, 'PNG', margin, 8, 35, 12);
    } catch (e) {
      console.warn('Marabes logo load failed', e);
    }
    if (axoBase64) {
      doc.addImage(axoBase64, 'PNG', pageWidth - margin - 35, 8, 35, 12);
    }

    // --- Divider line ---
    doc.setDrawColor(63, 81, 181);
    doc.setLineWidth(0.5);
    doc.line(margin, 23, pageWidth - margin, 23);

    // --- Title banner ---
    doc.setFillColor(232, 245, 255);
    doc.rect(margin, 26, contentWidth, 14, 'F');
    const bannerTextY = 35;

    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(`Date: ${today}`, margin + 3, bannerTextY);

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 58, 139);
    doc.text(getModalTitle(), pageWidth / 2, bannerTextY, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    const exportDataCount = (selectedMonth ? filteredRecords : records).length;
    const totalText = `Total: ${getTotalDuration()} | ${exportDataCount} Record${exportDataCount === 1 ? '' : 's'}`;
    doc.text(totalText, pageWidth - margin - 3 - doc.getTextWidth(totalText), bannerTextY);

    // --- Employee info ---
    let currentY = 48;
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'normal');
    doc.text(`Employee: ${employeeName || 'N/A'}`, margin, currentY);
    currentY += 5;
    const periodLabel = isAllTime ? 'Period: All Years' : `Period: ${selectedYear}${selectedMonth ? ` – ${monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth}` : ''}`;
    doc.text(periodLabel, margin, currentY);
    currentY += 8;

    // --- Table ---
    const isLateness = absenceType === 'LATENESS';
    const dataToExport = selectedMonth ? filteredRecords : records;

    const head = isLateness
      ? [['Type', 'Date', 'Minutes Late', 'Status']]
      : [['Type', 'Start Date', 'End Date', 'Duration', 'Reason', 'Status']];

    const body = dataToExport.map(record => {
      const typeLabel = getTypeLabel(record.ty_pe);
      const startDate = record.startDate
        ? new Date(record.startDate).toLocaleDateString('en-GB')
        : 'N/A';
      const endDate = record.endDate
        ? new Date(record.endDate).toLocaleDateString('en-GB')
        : 'N/A';
      const duration = getDurationLabel(record);
      const minutesLate = record.hours ? `${Math.round(record.hours * 60)} min` : 'N/A';
      const reason = record.reason || 'N/A';
      const status = getStatusConfig(record.status).label;

      return isLateness
        ? [typeLabel, startDate, minutesLate, status]
        : [typeLabel, startDate, endDate, duration, reason, status];
    });

    autoTable(doc, {
      startY: currentY,
      head,
      body,
      theme: 'grid',
      headStyles: {
        fillColor: [63, 81, 181],
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: 'bold',
      },
      styles: { fontSize: 8, cellPadding: 3 },
      alternateRowStyles: { fillColor: [245, 248, 255] },
      margin: { left: margin, right: margin },
    });

    // --- Footer ---
    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'italic');
    doc.text(`Generated on ${today} · Axo People HR System`, pageWidth / 2, finalY, { align: 'center' });

    // --- Save ---
    const safeType = (absenceType || 'absence').toLowerCase().replace(/_/g, '-');
    const safeName = (employeeName || 'employee').toLowerCase().replace(/\s+/g, '-');
    doc.save(`${safeType}-records-${safeName}-${selectedYear || 'all'}.pdf`);
  };

  return (
    <ThemeProvider theme={modalTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 400 }}
      >
        <StyledDialogTitle>
          <Box>
            <Typography variant="h6" component="div">
              {getModalTitle()}
            </Typography>
            <Typography variant="caption" sx={{ color: modalTheme.palette.mode === 'dark' ? modalTheme.palette.text.secondary : '#e0e0e0' }}>
              {employeeName} • {isAllTime ? 'All Years' : `Year: ${selectedYear}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={`${filteredRecords.length} Records`}
              color="secondary"
              size="small"
              sx={{
                background: modalTheme.palette.mode === 'dark' ? modalTheme.palette.background.default : `linear-gradient(135deg, ${modalTheme.palette.secondary.light} 0%, ${modalTheme.palette.secondary.dark} 100%)`,
                color: modalTheme.palette.mode === 'dark' ? modalTheme.palette.text.primary : '#ffffff',
                fontWeight: 500,
                border: modalTheme.palette.mode === 'dark' ? `1px solid ${modalTheme.palette.divider}` : 'none'
              }}
            />
            <Chip
              label={`Total: ${getTotalDuration()}`}
              color="primary"
              size="small"
              sx={{
                background: modalTheme.palette.mode === 'dark' ? modalTheme.palette.background.default : `linear-gradient(135deg, ${modalTheme.palette.primary.light} 0%, ${modalTheme.palette.primary.dark} 100%)`,
                color: modalTheme.palette.mode === 'dark' ? modalTheme.palette.text.primary : '#ffffff',
                fontWeight: 500,
                border: modalTheme.palette.mode === 'dark' ? `1px solid ${modalTheme.palette.divider}` : 'none'
              }}
            />
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ color: modalTheme.palette.mode === 'dark' ? modalTheme.palette.text.primary : '#ffffff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </StyledDialogTitle>

        <DialogContent
          sx={{
            pt: 3,
            pb: 4,
            backgroundColor: modalTheme.palette.background.default,
          }}
        >
          {/* TOOLBAR: Month filter + Export PDF */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            {!isAllTime && typeof selectedYear === 'number' ? (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Filter by Month</InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(e.target.value)}
                  label="Filter by Month"
                >
                  <MenuItem value="">All Months ({selectedYear})</MenuItem>
                  {monthOptions.map(month => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box />
            )}
            <ExportButtons
              onExportPDF={exportToPDF}
              disabled={loading || filteredRecords.length === 0}
            />
          </Box>

          {loading ? (
            <Fade in={loading}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 6,
                }}
              >
                <StyledCircularProgress />
                <Typography
                  variant="body2"
                  sx={{ ml: 2, color: modalTheme.palette.text.secondary }}
                >
                  Loading records...
                </Typography>
              </Box>
            </Fade>
          ) : error ? (
            <Fade in={!!error}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                  backgroundColor: modalTheme.palette.error.light,
                  borderRadius: 2,
                  color: modalTheme.palette.error.contrastText,
                }}
              >
                <Typography variant="body1">{error}</Typography>
              </Box>
            </Fade>
          ) : filteredRecords.length === 0 ? (
            <Fade in={filteredRecords.length === 0}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 6,
                  textAlign: 'center',
                  backgroundColor: modalTheme.palette.background.paper,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: '50%',
                    backgroundColor: modalTheme.palette.mode === 'dark' ? modalTheme.palette.background.default : modalTheme.palette.grey[100],
                  }}
                >
                  {absenceType === 'ANNUAL_LEAVE' ? (
                    <BeachAccess
                      sx={{
                        fontSize: 40,
                        color: modalTheme.palette.grey[500],
                      }}
                    />
                  ) : absenceType === 'SICKNESS' ? (
                    <Sick
                      sx={{
                        fontSize: 40,
                        color: modalTheme.palette.grey[500],
                      }}
                    />
                  ) : absenceType === 'PERSONAL' ? (
                    <Home
                      sx={{
                        fontSize: 40,
                        color: modalTheme.palette.grey[500],
                      }}
                    />
                  ) : absenceType === 'LATENESS' ? (
                    <AccessTime
                      sx={{
                        fontSize: 40,
                        color: modalTheme.palette.grey[500],
                      }}
                    />
                  ) : absenceType === 'SUSPENSION' ? (
                    <Gavel
                      sx={{
                        fontSize: 40,
                        color: modalTheme.palette.grey[500],
                      }}
                    />
                  ) : (
                    <BeachAccess
                      sx={{
                        fontSize: 40,
                        color: modalTheme.palette.grey[500],
                      }}
                    />
                  )}
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: modalTheme.palette.text.primary }}>
                  No Records Found
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: modalTheme.palette.text.secondary, maxWidth: 400 }}
                >
                  {selectedMonth
                    ? `No records found for ${monthOptions.find(m => m.value === selectedMonth)
                      ?.label || 'this month'
                    } ${selectedYear}.`
                    : isAllTime
                      ? `No ${getModalTitle().replace(' (All Time)', '').toLowerCase()} found across all years.`
                      : `No records found for ${selectedYear}.`
                  }
                </Typography>
              </Box>
            </Fade>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                overflow: 'auto',
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Start Date</strong>
                    </TableCell>
                    {absenceType !== 'LATENESS' && (
                      <TableCell>
                        <strong>End Date</strong>
                      </TableCell>
                    )}
                    {absenceType !== 'LATENESS' && (
                      <TableCell align="center">
                        <strong>Duration</strong>
                      </TableCell>
                    )}
                    <TableCell>
                      <strong>
                        {absenceType === 'LATENESS'
                          ? 'Minutes Late'
                          : 'Reason'}
                      </strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Status</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRecords.map((record, index) => {
                    const statusConfig = getStatusConfig(record.status);
                    return (
                      <Fade
                        key={record.id || index}
                        in
                        timeout={index * 100}
                      >
                        <StyledTableRow>
                          <TableCell>
                            <Chip
                              icon={getTypeIcon(record.ty_pe)}
                              label={getTypeLabel(record.ty_pe)}
                              color={getTypeColor(record.ty_pe)}
                              size="small"
                              sx={{
                                background: `linear-gradient(135deg, ${modalTheme.palette[getTypeColor(record.ty_pe)]
                                  ?.light || '#e0e0e0'
                                  } 0%, ${modalTheme.palette[getTypeColor(record.ty_pe)]
                                    ?.main || '#9e9e9e'
                                  } 100%)`,
                                color: '#ffffff',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <DateFormatter
                              date={record.startDate || record.date}
                            />
                          </TableCell>
                          {absenceType !== 'LATENESS' && (
                            <>
                              <TableCell>
                                <DateFormatter
                                  date={record.endDate || 'N/A'}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={getDurationLabel(record)}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor: modalTheme.palette.primary.main,
                                    color: modalTheme.palette.primary.main,
                                    fontWeight: 500,
                                  }}
                                />
                              </TableCell>
                            </>
                          )}
                          <TableCell>
                            {absenceType === 'LATENESS'
                              ? record.hours
                                ? `${Math.round(record.hours * 60)} minutes`
                                : 'N/A'
                              : record.reason || 'N/A'}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={statusConfig.label}
                              color={statusConfig.color}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor:
                                  statusConfig.color === 'success'
                                    ? modalTheme.palette.success.main
                                    : statusConfig.color === 'warning'
                                      ? modalTheme.palette.warning.main
                                      : statusConfig.color === 'error'
                                        ? modalTheme.palette.error.main
                                        : statusConfig.color === 'secondary'
                                          ? modalTheme.palette.secondary.main
                                          : modalTheme.palette.grey[400],
                                color:
                                  statusConfig.color === 'success'
                                    ? modalTheme.palette.success.main
                                    : statusConfig.color === 'warning'
                                      ? modalTheme.palette.warning.main
                                      : statusConfig.color === 'error'
                                        ? modalTheme.palette.error.main
                                        : statusConfig.color === 'secondary'
                                          ? modalTheme.palette.secondary.main
                                          : modalTheme.palette.text.secondary,
                              }}
                            />
                          </TableCell>
                        </StyledTableRow>
                      </Fade>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
};

export default AbsenceRecordsModal;
